import { CompaniesRepo } from '@/data/repos/companiesRepo';
import { ContactsRepo } from '@/data/repos/contactsRepo';
import { SitesRepo } from '@/data/repos/sitesRepo';
import { RefrigerationUnitsRepo } from '@/data/repos/refrigerationUnits';
import { ReportsRepo } from '@/data/repos/reportsRepo';
import { ActionsRepo } from '@/data/repos/actionsRepo';
import { TemplatesRepo } from '@/data/repos/templatesRepo';
import { catalogRepo } from '@/data/repos/catalogRepo';
import { settingsRepo } from '@/data/repos/settingsRepo';
import { getPendingSyncCount, getPendingSyncCounts, pullUpdates, pushOutbox } from '@/data/sync';
import { useSyncStatusStore } from '@/data/sync/syncStatusStore';
import { getWorkingSetStatus, refreshWorkingSet } from '@/data/offline/workingSetService';
import type { DataMode, DataProvider, ContactsProvider, SyncProvider } from './types';

function createContactsProvider(): ContactsProvider {
  return {
    listByCompany: (companyId) => ContactsRepo.listByCompany(companyId),
    listBySite: (companyId, siteId) => ContactsRepo.listBySite(companyId, siteId),
    add: (companyId, input) => ContactsRepo.add(companyId, input),
    update: (companyId, contactId, input) => ContactsRepo.update(companyId, contactId, input),
    remove: (companyId, contactId) => ContactsRepo.remove(companyId, contactId),
  };
}

function createSyncProvider(mode: DataMode): SyncProvider {
  if (mode === 'local') {
    return {
      getStatusSnapshot: () => ({
        syncing: false,
        lastSyncAt: null,
        lastError: null,
        lastConflict: null,
        log: [],
      }),
      getPendingCount: async () => 0,
      getPendingCounts: async () => ({ reports: 0, contacts: 0, photos: 0 }),
      retrySync: async () => {},
    };
  }

  return {
    getStatusSnapshot: () => {
      const { syncing, lastSyncAt, lastError, lastConflict, log } = useSyncStatusStore.getState();
      return { syncing, lastSyncAt, lastError, lastConflict, log };
    },
    getPendingCount: () => getPendingSyncCount(),
    getPendingCounts: () => getPendingSyncCounts(),
    retrySync: async () => {
      await pushOutbox(undefined, { force: true });
    },
  };
}

function buildProvider(mode: DataMode): DataProvider {
  return {
    mode,
    companies: CompaniesRepo,
    sites: SitesRepo,
    units: RefrigerationUnitsRepo,
    contacts: createContactsProvider(),
    reports: ReportsRepo,
    actions: ActionsRepo,
    templates: TemplatesRepo,
    catalogs: {
      getAllCatalogs: () => catalogRepo.getAllCatalogs(),
      getCatalog: (name) => catalogRepo.getCatalog(name),
      getCatalogVersion: () => catalogRepo.getCatalogVersion(),
      isUserModified: (name) => catalogRepo.isUserModified(name),
      saveCatalog: (catalog) => catalogRepo.saveCatalog(catalog),
      setCatalogVersion: (version) => catalogRepo.setCatalogVersion(version),
      addCatalogOption: (name, option) => catalogRepo.addCatalogOption(name, option),
      updateCatalogOption: (name, optionId, updates) =>
        catalogRepo.updateCatalogOption(name, optionId, updates),
      removeCatalogOption: (name, optionId) => catalogRepo.removeCatalogOption(name, optionId),
      exportCatalogs: () => catalogRepo.exportCatalogs(),
      importCatalogs: (payload) => catalogRepo.importCatalogs(payload),
    },
    settings: settingsRepo,
    workingSet: {
      getStatus: () => getWorkingSetStatus(),
      refresh: async () => {
        if (mode === 'synced') {
          try {
            await pullUpdates();
          } catch {
            // Ignore pull failures and still refresh local counts.
          }
        }
        return refreshWorkingSet();
      },
    },
    sync: createSyncProvider(mode),
  };
}

export function createLocalProvider(): DataProvider {
  return buildProvider('local');
}

export function createSyncedProvider(): DataProvider {
  return buildProvider('synced');
}

export function createDataProvider(mode: DataMode): DataProvider {
  return buildProvider(mode);
}
